var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import * as zIndex from '@tencent/exeditor3-ui-kit/dist/zIndex.js';
import { raf, styled, fixFloatNumber, calculateDisplayRect, px, } from './utils.js';
import { createResizer, createCropper, } from '@tencent/exeditor3-ui-kit/dist/resizer/index.js';
import './picture-toolbar.js';
let ExEditorPictureBlockContainerElement = class ExEditorPictureBlockContainerElement extends LitElement {
    constructor() {
        super(...arguments);
        this.editable = false;
        this.allowFloat = false;
        this.width = 0;
        this.height = 0;
        this.rotate = 0;
        this.float = '';
        this.desc = '';
        // 图像原始属性
        this.originWidth = 0;
        this.originHeight = 0;
        // 图像旋转偏移量
        this.dw = 0;
        this.dh = 0;
        this.active = false;
        this.min = 30;
        this.t = (token, fallback) => fallback !== null && fallback !== void 0 ? fallback : token;
        this.isCropping = false;
        this.loaded = false;
        this.resizer = null;
        this.cropper = null;
        this.handleCustomResize = (detail) => {
            const { width, height, crop } = this;
            const scaleX = detail.scale.x;
            const scaleY = detail.scale.y;
            this.width = width * scaleX;
            this.height = height * scaleY;
            if (crop) {
                this.crop = {
                    width: detail.width,
                    height: detail.height,
                    x: crop.x * scaleX,
                    y: crop.y * scaleY,
                };
            }
            this.triggerChange();
        };
        this.handleCustomCropResize = (detail) => {
            this.width = detail.width;
            this.height = detail.height;
            if (!this.crop) {
                this.crop = { width: 0, height: 0, x: 0, y: 0 };
            }
            this.crop.x += detail.x;
            this.crop.y += detail.y;
            this.triggerChange();
        };
        this.handleCustomMove = (detail) => {
            Object.assign(this.originImage.style, {
                left: px(detail.left),
                top: px(detail.top),
            });
        };
        this.handleCustomCrop = (detail) => {
            var _a, _b;
            this.crop = {
                width: detail.width,
                height: detail.height,
                x: (((_a = this.crop) === null || _a === void 0 ? void 0 : _a.x) || 0) - detail.x,
                y: (((_b = this.crop) === null || _b === void 0 ? void 0 : _b.y) || 0) - detail.y,
            };
            this.triggerChange();
        };
        this.handleImageLoad = async () => {
            let { width, height } = this;
            const { naturalWidth: originWidth, naturalHeight: originHeight } = this.originImage;
            if (width && !height) {
                height = (width / originWidth) * originHeight;
            }
            else if (!width && !height) {
                width = originWidth;
                height = originHeight;
            }
            this.originWidth = width;
            this.originHeight = height;
            this.width = width;
            this.height = height;
            this.loaded = true;
            this.triggerChange('load');
            if (this.active) {
                this.toggleResizer(true);
            }
        };
        this.handleResizerDoubleClick = (event) => {
            event.preventDefault();
            this.toggleCropper(true);
        };
    }
    render() {
        const { originWidth, originHeight, rotate, crop } = this;
        let { width, height } = this;
        let viewStyle = '';
        let imgStyle = '';
        let placeholderStyle = '';
        if (!width && !height) {
            width = originWidth;
            height = originHeight;
        }
        else if (width) {
            height = width / (originWidth / originHeight);
        }
        if (!this.loaded) {
            placeholderStyle = styled({
                display: 'none',
            });
            viewStyle = styled({
                position: 'relative',
                'max-width': '100%',
            });
            imgStyle = styled({
                position: 'relative',
            });
        }
        else if (!rotate && crop) {
            placeholderStyle = styled({
                display: 'none',
            });
            viewStyle = styled({
                width: px(crop.width || width),
                height: px(crop.height || height),
            });
            imgStyle = styled({
                position: 'relative',
                width: px(this.width),
                height: 'auto',
                left: px(crop.x),
                top: px(crop.y),
            });
        }
        else {
            const result = calculateDisplayRect({
                width,
                height,
                rotate,
                crop,
            });
            viewStyle = styled({
                position: 'absolute',
                left: px(result.offsetX),
                top: px(result.offsetY),
                width: px(result.realWidth),
                height: px(result.realHeight),
                overflow: 'hidden',
                'transform-origin': '0 0',
                transform: rotate ? `rotate(${fixFloatNumber(rotate, 4)}rad)` : '',
            });
            imgStyle = styled({
                display: 'block',
                position: 'absolute',
                left: px(result.x),
                top: px(result.y),
            });
            placeholderStyle = styled({
                width: px(result.width),
                height: px(result.height),
            });
            this.dw = result.offsetX;
            this.dh = result.offsetY;
        }
        // 更新
        raf(() => {
            var _a, _b;
            (_a = this.updateResizer) === null || _a === void 0 ? void 0 : _a.call(this, this);
            (_b = this.updateCropper) === null || _b === void 0 ? void 0 : _b.call(this, this);
        });
        return html `
      <div class="placeholder" style="${placeholderStyle}"></div>

      <div class="view" style="${viewStyle}">
        <img
          class="origin"
          src=${this.src}
          title=${this.desc}
          width=${this.width || false}
          style="${imgStyle}"
          @load=${this.handleImageLoad}
        />
      </div>

      ${this.active && this.editable && this.loaded
            ? html `
            <exeditor-picture-toolbar
              class="toolbar"
              ?crop=${this.isCropping}
              float=${this.float}
              ?allowFloat=${this.allowFloat}
              @toolclick=${this.handleToolClick}
              .t=${this.t}
            ></exeditor-picture-toolbar>
          `
            : null}
    `;
    }
    handleToolClick(event) {
        const { type, value } = event.detail;
        if (type === 'remove') {
            this.triggerChange('remove');
        }
        else if (type === 'restore') {
            this.restoreImage();
        }
        else if (type === 'rotate') {
            this.doRotate(Number(value));
        }
        else if (type === 'crop') {
            const active = !this.isCropping;
            this.toggleCropper(active);
            if (!active) {
                this.toggleResizer(true);
            }
        }
        else if (type === 'float') {
            this.changeFloat(String(value));
        }
    }
    attributeChangedCallback(name, oldValue, value) {
        super.attributeChangedCallback(name, oldValue, value);
        if (name === 'active' && oldValue !== value) {
            const active = value !== null;
            if (this.loaded) {
                this.toggleResizer(active);
            }
            if (!active) {
                this.toggleCropper(false);
            }
        }
    }
    toggleResizer(active) {
        var _a;
        const root = this.shadowRoot;
        if (!root || !this.editable) {
            return;
        }
        if (active) {
            if (!this.resizer) {
                const { resizer, update } = createResizer({
                    target: this.showcase,
                    onResizeEnd: this.handleCustomResize,
                    keepAspectRatio: true,
                });
                this.resizer = resizer;
                this.updateResizer = update;
                this.resizer.addEventListener('dblclick', this.handleResizerDoubleClick);
                root.appendChild(resizer);
            }
            this.showcase.addEventListener('dblclick', this.handleResizerDoubleClick);
            this.resizer.min = this.min;
            (_a = this.updateResizer) === null || _a === void 0 ? void 0 : _a.call(this, this);
            this.resizer.style.display = 'block';
        }
        else {
            if (!this.resizer) {
                return;
            }
            this.showcase.removeEventListener('dblclick', this.handleResizerDoubleClick);
            this.resizer.style.display = 'none';
        }
    }
    toggleCropper(active) {
        var _a;
        const root = this.shadowRoot;
        if (!root || !this.editable) {
            return;
        }
        this.isCropping = active;
        if (active) {
            this.toggleResizer(false);
            if (!this.cropper) {
                const { cropper, update } = createCropper({
                    targetMove: this.originImage,
                    targetCrop: this.showcase,
                    onMove: this.handleCustomMove,
                    onCrop: this.handleCustomCrop,
                    onResizeEnd: this.handleCustomCropResize,
                    keepAspectRatio: true,
                });
                this.cropper = cropper;
                const image = this.cropper.appendChild(document.createElement('img'));
                image.src = this.src;
                Object.assign(image.style, {
                    width: '100%',
                    height: '100%',
                });
                this.updateCropper = update;
                root.appendChild(cropper);
            }
            this.cropper.min = this.min;
            this.cropper.max = this.max;
            (_a = this.updateCropper) === null || _a === void 0 ? void 0 : _a.call(this, this);
            this.cropper.style.display = 'block';
        }
        else {
            if (!this.cropper) {
                return;
            }
            this.cropper.style.display = 'none';
        }
    }
    restoreImage() {
        this.isCropping = false;
        this.toggleCropper(false);
        this.toggleResizer(true);
        this.width = this.originImage.naturalWidth;
        this.height = this.originImage.naturalHeight;
        this.rotate = 0;
        this.crop = null;
        this.triggerChange('restore');
    }
    triggerChange(name = 'change') {
        const event = new CustomEvent(name);
        this.dispatchEvent(event);
    }
    doRotate(rad) {
        let rotate = (this.rotate || 0) + rad;
        // NOTE: Math.sin/cos -> 0 误差大， -> 1 误差小
        if (Math.cos(rotate) === 1) {
            rotate = 0;
        }
        this.rotate = rotate % (Math.PI * 2);
        this.triggerChange();
    }
    changeFloat(float) {
        this.float = float;
        this.triggerChange();
    }
};
ExEditorPictureBlockContainerElement.styles = [
    css `
      :host {
        position: relative;
        display: block;
        max-width: 100%;
        white-space: normal;
      }
      .placeholder {
        position: relative;
        pointer-events: none;
      }
      .view {
        position: relative;
        top: 0;
        left: 0;
        overflow: hidden;
        transform-origin: 0 0;
      }
      .origin {
        display: block;
        position: absolute;
      }
      .toolbar {
        position: absolute;
        top: -2.5em;
        padding: 2px;
        border-radius: 4px;
        z-index: ${zIndex.tooltip};
      }
    `,
];
__decorate([
    query('.view')
], ExEditorPictureBlockContainerElement.prototype, "showcase", void 0);
__decorate([
    query('.origin')
], ExEditorPictureBlockContainerElement.prototype, "originImage", void 0);
__decorate([
    property({
        reflect: true,
        type: Boolean,
    })
], ExEditorPictureBlockContainerElement.prototype, "editable", void 0);
__decorate([
    property({
        reflect: true,
        type: Boolean,
    })
], ExEditorPictureBlockContainerElement.prototype, "allowFloat", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "width", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "height", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "rotate", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Object,
    })
], ExEditorPictureBlockContainerElement.prototype, "crop", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: String,
    })
], ExEditorPictureBlockContainerElement.prototype, "float", void 0);
__decorate([
    property({
        attribute: true,
    })
], ExEditorPictureBlockContainerElement.prototype, "desc", void 0);
__decorate([
    property({
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "originWidth", void 0);
__decorate([
    property({
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "originHeight", void 0);
__decorate([
    property({
        reflect: true,
    })
], ExEditorPictureBlockContainerElement.prototype, "src", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Boolean,
    })
], ExEditorPictureBlockContainerElement.prototype, "active", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "min", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorPictureBlockContainerElement.prototype, "max", void 0);
__decorate([
    property()
], ExEditorPictureBlockContainerElement.prototype, "t", void 0);
__decorate([
    state()
], ExEditorPictureBlockContainerElement.prototype, "isCropping", void 0);
__decorate([
    state()
], ExEditorPictureBlockContainerElement.prototype, "loaded", void 0);
ExEditorPictureBlockContainerElement = __decorate([
    customElement('exeditor-picture-block-container')
], ExEditorPictureBlockContainerElement);
export { ExEditorPictureBlockContainerElement };
//# sourceMappingURL=picture-block-container.js.map